import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../../components/Layout.js';
import { Seo, buildBreadcrumbList } from '../../components/Seo.js';
import { RichText } from '../../components/RichText.js';
import { api } from '../../lib/api.js';
import { trackViewEvent } from '../../lib/analytics.js';
import { stripInlineMarks } from '../../lib/markdown.js';

interface PublicEventResponse {
  org: { id: string; name: string; slug: string; logoUrl?: string };
  event: {
    id: string;
    slug: string;
    status: string;
    startsAt: string;
    endsAt: string;
    timezone: string;
    bannerUrl: string | null;
    currency: string;
    serviceFeePct: number;
    publishedAt: string | null;
    createdAt: string;
    title: string;
    description: string;
    venue: string;
  };
  ticketTypes: Array<{
    id: string;
    kind: 'entry' | 'combo' | 'seat' | 'consumption';
    priceCents: number;
    capacity: number;
    sold: number;
    remaining: number;
    maxPerOrder: number | null;
    name: string;
    description: string;
  }>;
}

export function PublicEventPage() {
  const { orgSlug, eventSlug, lang } = useParams();
  const { t, i18n } = useTranslation('event');
  const { t: tDiscover } = useTranslation('discover');

  const { data, isLoading } = useQuery({
    queryKey: ['public-event', orgSlug, eventSlug, lang],
    queryFn: () =>
      api<PublicEventResponse>(`/events/by-slug/${orgSlug}/${eventSlug}?locale=${lang}`),
    enabled: Boolean(orgSlug && eventSlug),
  });

  // Hint the browser to fetch the banner with high priority once we know
  // the URL — improves LCP on the public event page. Injected as
  // <link rel="preload"> via JS because the SPA shell does not know the
  // banner URL ahead of time.
  useEffect(() => {
    const bannerUrl = data?.event.bannerUrl;
    if (!bannerUrl) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = bannerUrl;
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('data-boletra-preload', 'banner');
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [data?.event.bannerUrl]);

  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // GA4 view_item once the event payload is loaded.
  useEffect(() => {
    if (!data) return;
    trackViewEvent({
      eventId: data.event.id,
      eventName: data.event.title,
      orgSlug: data.org.slug,
    });
  }, [data]);
  const totalQty = useMemo(
    () => Object.values(quantities).reduce((sum, n) => sum + n, 0),
    [quantities],
  );
  const subtotal = useMemo(() => {
    if (!data) return 0;
    return data.ticketTypes.reduce((sum, tt) => sum + (quantities[tt.id] ?? 0) * tt.priceCents, 0);
  }, [data, quantities]);

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center text-neutral-400">Loading…</div>
      </Layout>
    );
  }

  const fmt = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: data.event.currency,
  });

  // schema.org Event.description debe ser texto plano — sin markup ni HTML.
  const plainDescription = stripInlineMarks(data.event.description);

  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'https://boletra.com';

  // Offer.validFrom: cuándo empieza a estar disponible esta oferta. Si el
  // evento ya está `live`, anclamos al `publishedAt`; si no, al `createdAt`.
  // Si por alguna razón ninguno está, caemos al `startsAt` del evento (peor
  // caso conservador). Offer.validThrough: hasta cuándo es comprable; lo
  // amarramos a `startsAt` (no aceptamos compras una vez arranca el show).
  const offerValidFrom = data.event.publishedAt ?? data.event.createdAt ?? data.event.startsAt;
  const offerValidThrough = data.event.startsAt;

  // image: si no hay banner, usamos el OG default del sitio. Google rechaza
  // Event sin `image` aunque el resto sea válido (rich result requirement).
  const ldImage = data.event.bannerUrl ?? `${origin}/og-default.jpg`;

  // performer: requerido por Google para Event rich results. No tenemos un
  // campo dedicado a artistas en el modelo (TODO #seo-performers para
  // separarlo), así que el organizer hace de PerformingGroup. Es un
  // approximation aceptable mientras no haya línea para capturar talent
  // por separado, y satisface a Search Console sin pintar info falsa.
  const performerLd = {
    '@type': 'PerformingGroup' as const,
    name: data.org.name,
    url: `${origin}/${lang}/o/${data.org.slug}`,
  };

  const eventLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: data.event.title,
    description: plainDescription,
    startDate: data.event.startsAt,
    endDate: data.event.endsAt,
    eventStatus:
      data.event.status === 'cancelled'
        ? 'https://schema.org/EventCancelled'
        : 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    image: ldImage,
    location: data.event.venue
      ? {
          '@type': 'Place',
          name: data.event.venue,
          address: data.event.venue,
        }
      : undefined,
    organizer: {
      '@type': 'Organization',
      name: data.org.name,
      url: `${origin}/${lang}/o/${data.org.slug}`,
    },
    performer: performerLd,
    offers: data.ticketTypes.map((tt) => ({
      '@type': 'Offer',
      name: tt.name,
      price: (tt.priceCents / 100).toFixed(2),
      priceCurrency: data.event.currency,
      availability: tt.remaining > 0 ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
      url: `${origin}/${lang}/o/${data.org.slug}/${data.event.slug}`,
      validFrom: offerValidFrom,
      validThrough: offerValidThrough,
    })),
  };

  const seoTitle = `${data.event.title} · ${data.org.name}`;
  const seoDescription =
    plainDescription.slice(0, 200).replace(/\s+/g, ' ').trim() ||
    (i18n.language === 'es'
      ? `Comprá tu boleto para ${data.event.title} en ${data.event.venue || 'Boletra'}.`
      : `Buy your ticket for ${data.event.title} at ${data.event.venue || 'Boletra'}.`);

  const breadcrumbLd = buildBreadcrumbList([
    { name: tDiscover('title'), url: `${origin}/${lang}/discover` },
    { name: data.org.name, url: `${origin}/${lang}/o/${data.org.slug}` },
    { name: data.event.title, url: `${origin}/${lang}/o/${data.org.slug}/${data.event.slug}` },
  ]);

  return (
    <Layout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        ogType="article"
        ogImage={data.event.bannerUrl ?? undefined}
        locale={(lang as 'es' | 'en') ?? 'es'}
        jsonLd={[eventLd, breadcrumbLd]}
      />
      <div className="max-w-3xl mx-auto px-4 pt-12 pb-24">
        <img
          src={data.event.bannerUrl ?? '/event-placeholder.jpg'}
          alt={data.event.title}
          loading="eager"
          decoding="async"
          // @ts-expect-error fetchpriority is valid in HTML but not yet
          // in @types/react; tells browsers to prioritize this LCP image.
          fetchpriority="high"
          className="w-full aspect-[2/1] object-cover rounded-2xl mb-8"
        />
        <h1 className="text-4xl font-bold mb-2">{data.event.title}</h1>
        <p className="text-neutral-400 mb-1">
          {t('public.venue')}: {data.event.venue || '—'}
        </p>
        <p className="text-neutral-400 mb-8">
          {t('public.starts_at')}:{' '}
          {new Intl.DateTimeFormat(i18n.language, {
            dateStyle: 'long',
            timeStyle: 'short',
          }).format(new Date(data.event.startsAt))}
        </p>
        <RichText
          text={data.event.description}
          className="text-neutral-300 leading-relaxed mb-12"
        />

        <h2 className="text-xl font-semibold mb-4">{t('public.select_tickets')}</h2>
        <div className="space-y-3 mb-8">
          {data.ticketTypes.map((tt) => {
            const maxQty = Math.min(tt.remaining, tt.maxPerOrder ?? tt.remaining);
            return (
              <div key={tt.id} className="card flex items-center gap-4">
                <div className="flex-1">
                  <p className="font-semibold">{tt.name}</p>
                  {tt.description && (
                    <p className="text-sm text-neutral-400 mt-1">{tt.description}</p>
                  )}
                  {tt.maxPerOrder != null && tt.remaining > 0 && (
                    <p className="text-xs text-neutral-500 mt-2">
                      {t('public.max_per_order', { count: tt.maxPerOrder })}
                    </p>
                  )}
                  {tt.remaining < 10 && tt.remaining > 0 && (
                    <p className="text-xs text-amber-400 mt-2">
                      {t('public.low_stock', { count: tt.remaining })}
                    </p>
                  )}
                  {tt.remaining === 0 && (
                    <p className="text-xs text-red-400 mt-2">{t('public.sold_out')}</p>
                  )}
                </div>
                <p className="font-semibold whitespace-nowrap">
                  {tt.priceCents === 0 ? t('public.free_price') : fmt.format(tt.priceCents / 100)}
                </p>
                <input
                  type="number"
                  min={0}
                  max={maxQty}
                  disabled={tt.remaining === 0}
                  value={quantities[tt.id] ?? 0}
                  onChange={(e) =>
                    setQuantities({
                      ...quantities,
                      [tt.id]: Math.max(0, Math.min(maxQty, Number(e.target.value))),
                    })
                  }
                  className="input w-20 text-center"
                />
              </div>
            );
          })}
        </div>

        <div className="card flex items-center justify-between">
          <div>
            <p className="text-sm text-neutral-400">
              {t('public.tickets_summary', { count: totalQty })}
            </p>
            <p className="text-2xl font-bold mt-1">{fmt.format(subtotal / 100)}</p>
          </div>
          <Link
            to={`/${lang}/o/${orgSlug}/${eventSlug}/checkout?items=${encodeQuantities(quantities)}`}
            className={
              totalQty > 0 ? 'btn-primary' : 'btn-secondary pointer-events-none opacity-50'
            }
          >
            {t('public.checkout')}
          </Link>
        </div>
      </div>
    </Layout>
  );
}

function encodeQuantities(q: Record<string, number>): string {
  return Object.entries(q)
    .filter(([, n]) => n > 0)
    .map(([id, n]) => `${id}:${n}`)
    .join(',');
}
